import { Autocomplete, Chip, CircularProgress, FormHelperText, Modal, Stack } from "@mui/material";
import { assignTemplate, getTemplateById, fetchUsers } from "apis/coachup";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { Form, Formik } from "formik";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { enqueueSnackbar } from "notistack";
import Loader from "utils/Loader";
import * as Yup from "yup";

const validationSchema = Yup.object({
  users: Yup.array(),
});

function AssignTemplate({ open, setOpen, template, setTemplate, setPayload, selectedTab }) {
  const loginUser = useSelector((state) => state.loginUser.loginUserState);
  const [users, setUsers] = useState([]);
  const [initialValues, setInitialValues] = useState({
    users: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [httpError, setHttpError] = useState({ error: false, message: "" });
  const [templateData, setTemplateData] = useState();

  const [controller] = useMaterialUIController();

  const handleClose = () => {
    if (!submitLoading) {
      setInitialValues({
        users: [],
      });
      setTemplate();
      setHttpError({ error: false, message: "" });
      setOpen(false);
      setTemplateData();
    }
  };

  const getTemplate = async () => {
    try {
      setIsLoading(true);
      const response = await getTemplateById(template);
      if (response?.data?.success) {
        const userIdArray = [];

        response?.data?.data?.CoachupUserTemplates?.forEach((user) => {
          if (user?.assignedBy === loginUser?.id) {
            userIdArray.push(user.userId);
          }
        });
        setInitialValues({ users: userIdArray });
        setTemplateData(response?.data?.data);
      } else if (response?.response?.status !== 403) {
        handleClose();
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      } else if (response?.response?.status === 403) {
        handleClose();
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const getUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await fetchUsers();
      if (response?.data?.success) {
        setUsers(response.data.data);
      } else if (response?.response?.status !== 403) {
        handleClose();
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: response?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      } else if (response?.response?.status === 403) {
        handleClose();
      }
      setUsersLoading(false);
    } catch (error) {
      setUsersLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const { darkMode } = controller;

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: window.innerWidth < 600 ? "90%" : 700,
    maxHeight: window.innerHeight < 800 ? 600 : 700,
    bgcolor: darkMode ? "#1F283E" : "background.paper",
    overflow: "auto",
    boxShadow: 24,
    borderRadius: "0.5rem",
    p: 4,
    outline: "none",
  };

  useEffect(() => {
    if (open) {
      getTemplate();
      getUsers();
    }
  }, [open]);

  const handleSubmit = async (data) => {
    try {
      setSubmitLoading(true);

      const removeUserAssignIds = initialValues.users.filter(
        (userId) => !data.users.includes(userId)
      );

      const assignmentList = data.users
        .filter((userId) => !initialValues.users.includes(userId))
        .map((user) => ({ id: user }));

      const response = await assignTemplate(template, { removeUserAssignIds, assignmentList });
      if (response?.data?.success) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: response.data.message || "Record Updated Successfully",
          }),
          {
            variant: "custom",
          }
        );
        handleClose();
        if (
          (selectedTab === 1 && assignmentList?.length > 0) ||
          (selectedTab === 0 && !assignmentList?.length)
        ) {
          setPayload((prevState) => ({ ...prevState, pageNumber: 0 }));
        }
      } else if (response?.response?.status !== 403) {
        setHttpError({
          error: true,
          message: response?.response?.data?.message || "Something Went Wrong",
        });
      } else if (response?.response?.status === 403) {
        handleClose();
      }

      setSubmitLoading(false);
    } catch (error) {
      setSubmitLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox sx={style}>
        <MDBox sx={{ position: "relative" }}>
          <Loader loading={isLoading} />
          <MDTypography variant="h4" mb={2}>
            Assign Template
          </MDTypography>

          <MDBox sx={{ position: "relative" }}>
            <Formik
              enableReinitialize
              validateOnChange
              validationSchema={validationSchema}
              initialValues={initialValues}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue, touched, errors, dirty }) => (
                <Form>
                  <Stack
                    spacing={2}
                    sx={{
                      "& .MuiFormHelperText-root": { color: "#d32f2f" },
                    }}
                  >
                    <Autocomplete
                      fullWidth
                      id="assign_to"
                      multiple
                      disableClearable
                      disableCloseOnSelect
                      options={users.filter((user) => {
                        if (user?.UserCompaniesLinks?.disabledAt) return false;
                        if (
                          templateData?.CoachupUserTemplates?.find(
                            (templateUser) =>
                              templateUser?.assignedBy !== loginUser?.id &&
                              templateUser?.userId === user?.id
                          )
                        ) {
                          return false;
                        }
                        if (user?.id === loginUser?.id) {
                          return false;
                        }
                        return true;
                      })}
                      onChange={(_, value) => {
                        setFieldValue(
                          "users",
                          value.map((user) => user.id)
                        );
                      }}
                      renderOption={(props, option) => (
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        <li {...props} key={option.id}>
                          {option.name}
                        </li>
                      )}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => (
                          <Chip
                            label={option.name}
                            {...getTagProps({ index })}
                            disabled={option?.UserCompaniesLinks?.disabledAt}
                          />
                        ))
                      }
                      isOptionEqualToValue={(option, value) => option?.id === value?.id}
                      getOptionLabel={(option) => option?.name}
                      value={users.filter((user) => values.users.includes(user.id)) || []}
                      renderInput={(params) => (
                        <MDInput
                          {...params}
                          placeholder="Assign To"
                          label="Assign To"
                          error={touched?.users && Boolean(errors?.users)}
                          helperText={touched?.users && errors?.users}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {usersLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />

                    {httpError.error && (
                      <MDBox
                        sx={{
                          "& .MuiFormHelperText-root": {
                            color: "#d32f2f",
                            fontSize: "16px",
                            mt: 2,
                            pl: 1,
                          },
                        }}
                      >
                        {" "}
                        <FormHelperText sx={{ marginTop: "0px !important" }}>
                          {httpError.message}
                        </FormHelperText>
                      </MDBox>
                    )}

                    <MDBox sx={{ display: "flex", justifyContent: "end", mt: 1 }}>
                      <MDButton
                        color="gray80"
                        size="small"
                        onClick={handleClose}
                        sx={{ mt: 1, mr: 1, position: "relative" }}
                      >
                        Cancel
                      </MDButton>
                      <MDButton
                        color="primaryRed"
                        size="small"
                        type="submit"
                        disabled={submitLoading || !dirty}
                        sx={{ mt: 1, position: "relative" }}
                      >
                        {submitLoading ? "Loading..." : "Save"}
                      </MDButton>
                    </MDBox>
                  </Stack>
                </Form>
              )}
            </Formik>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
}

export default AssignTemplate;
